import mapKeysDeep from '@kikobeats/map-keys-deep';
import _, { camelCase, snakeCase } from 'lodash';
import { DateTime } from 'luxon';
import { defer, from, identity, Observable, of } from 'rxjs';
import { CamelCasedPropertiesDeep } from 'type-fest/camel-cased-properties-deep';
import { SnakeCasedPropertiesDeep } from 'type-fest/snake-cased-properties-deep';
import {
  isNonNullable,
  LocationObj,
  Obj,
  Opt,
  ReactRouterHistoryLocationObject,
  Unit,
  Unit$,
} from './general.types';

export const lazyOf = <T>(input: () => T): Observable<T> =>
  defer(() => of(input()));
export const lazyFrom = <T>(
  input: () => PromiseLike<T>
): Observable<T> => defer(() => from(input()));

export const newUnit: () => Unit = () => ({});
export const newUnit$: Unit$ = lazyOf(newUnit);
export const BLANK$ = of(undefined);

export const inEnum = <
  T extends { [key: string]: string },
  K extends keyof T & string
>(
  value: string,
  enumObject: T
): Opt<T[K]> => {
  if (Object.values(enumObject).includes(value)) {
    return value as T[K];
  } else {
    return undefined;
  }
};

/** Maps 'none' -> '' and passes all other strings unchanged */
export const noneToEmpty = (string: string): string =>
  string.toLowerCase().trim() === 'none' ? '' : string;

export const makeArray = <T>(input: T | T[]): T[] =>
  _.isArray(input) ? input : [input];

export const toOptNumber = (input?: string): Opt<number> =>
  input ? Number(input) : undefined;

export const toStringPassNullable = (
  input?: unknown
): string | undefined | null =>
  isNonNullable(input) ? String(input) : input;

export const median = (arr: number[]): number => {
  arr.sort((a, b) => a - b);
  const half = Math.floor(arr.length / 2);
  if (arr.length % 2 === 1) {
    return arr[half];
  }
  return (arr[half - 1] + arr[half]) / 2;
};

export const hashCode = (str: string): number => {
  let hash = 0;
  let i = 0;
  const len = str.length;
  while (i < len) {
    // eslint-disable-next-line no-bitwise, no-plusplus
    hash = ((hash << 5) - hash + str.charCodeAt(i++)) << 0;
  }
  return hash + 2147483647 + 1;
};

/**
 * Adds https:// to the start of `url` if it doesn't already start
 * with https:// or http://
 * @param url url string to check / modify if needed
 * @returns url, modified as appropriate
 */
export const withHttps = (url: string): string =>
  !/^https?:\/\//i.test(url) ? `https://${url}` : url;

export const sentenceCase = (input: string): string =>
  _.capitalize(_.lowerCase(input));

type OptionalHistoryObj<T> = T extends undefined
  ? undefined
  : ReactRouterHistoryLocationObject;
export const getHistoryLocationObj = <
  T extends Opt<string | LocationObj>
>(
  to?: T
): OptionalHistoryObj<T> => {
  if (typeof to === 'object') {
    const fieldCorrected = {
      pathname: to.pathname,
      search: to.query,
    };
    return _.mapValues(fieldCorrected, v =>
      typeof v === 'string' ? v : new URLSearchParams(v).toString()
    ) as OptionalHistoryObj<T>;
  } else {
    return to as OptionalHistoryObj<T>;
  }
};

export const keysToSnakeCase = <T>(
  value: T
): SnakeCasedPropertiesDeep<T> =>
  mapKeysDeep(value, (v, k) =>
    typeof k === 'string' ? snakeCase(k) : k
  );
export const keysToCamelCase = <T>(
  value: T
): CamelCasedPropertiesDeep<T> =>
  mapKeysDeep(value, (v, k) =>
    typeof k === 'string' ? camelCase(k) : k
  );

type ValuesHaveKey<N extends string> = {
  [x: string]: { [key in N]: unknown };
};
type PluckValuesKey<T extends ValuesHaveKey<N>, N extends string> = {
  [K in keyof T]: T[K][N];
};
export function getReducers<T extends ValuesHaveKey<'reducer'>>(
  slices: T
): PluckValuesKey<T, 'reducer'> {
  return _.mapValues(
    slices,
    slice => slice.reducer
  ) as PluckValuesKey<T, 'reducer'>;
}
export function getActions<T extends ValuesHaveKey<'actions'>>(
  slices: T
): PluckValuesKey<T, 'actions'> {
  return _.mapValues(
    slices,
    slice => slice.actions
  ) as PluckValuesKey<T, 'actions'>;
}

type SafeMapGetRtn<
  T extends Obj,
  K extends keyof T | string | undefined
> = K extends undefined
  ? undefined
  : K extends keyof T | `${Exclude<keyof T, symbol>}`
  ? T[K]
  : T[keyof T] | undefined;
export class SafeMap<T extends Obj = Obj<never>> {
  public readonly mapObj: T;

  constructor(inputObj: T | undefined) {
    this.mapObj = inputObj || ({} as T);
  }

  get<K extends keyof T | string | undefined>(
    key: K
  ): SafeMapGetRtn<T, K> {
    if (key === undefined) return undefined as SafeMapGetRtn<T, K>;
    const rtn =
      (typeof key === 'string' || typeof key === 'number') &&
      (key in this.mapObj || String(key) in this.mapObj)
        ? this.mapObj[key]
        : undefined;
    return rtn as SafeMapGetRtn<T, K>;
  }
}

export function jsxContains(
  elementIn: JSX.Element | string | null,
  type: string
): boolean {
  const element = elementIn || ({} as Obj);
  if (!element || typeof element === 'string') return false;
  if (element?.type === type) return true;

  const children: unknown = _.get(element, 'props.children');
  if (!children || typeof children === 'string') return false;

  let childrenArr: (JSX.Element | null)[] = [];
  if (_.isArray(children)) {
    childrenArr = children as (JSX.Element | null)[];
  } else {
    childrenArr = [children as JSX.Element | null];
  }
  return childrenArr
    .map(child => jsxContains(child, type))
    .some(identity);
}

/**
 * Checks if inputted code is a valid barcode in one of the selected
 * formats: EAN-13, UPC-A, EAN-8, UPC-E.
 * @param barcode - input code as a string
 * @returns true if barcode is valid
 */
export const isValidBarcode = (barcode?: string | null): boolean => {
  if (!barcode) return false;
  const digits = () => /^\d{8,13}$/g.test(barcode);
  const validLengths = [8, 12, 13];

  if (!digits() || !validLengths.includes(barcode.length)) {
    return false;
  }

  let checksum = 0;
  const codeList = barcode.split('');
  const checkDigit = parseInt(codeList.pop() as string, 10);
  codeList.forEach((value, index) => {
    const digit = parseInt(value, 10);
    if (barcode.length % 2 === 1) {
      checksum += index % 2 ? digit * 3 : digit;
    } else {
      checksum += index % 2 ? digit : digit * 3;
    }
  });

  let check = checksum % 10;
  if (check !== 0) check = 10 - check;
  if (check === checkDigit) return true;
  return false;
};

type UnitsRemainingProps = {
  startDate: DateTime;
  monthsDuration?: number;
};
type TimeUnit = 'days' | 'months' | 'years';
export type TimeAmount = {
  duration: number;
  units: TimeUnit;
};

export const formatUnits = ({
  units,
  duration,
}: TimeAmount): string =>
  duration === 1 ? units.replace(/s$/, '') : units;

/**
 * Gets the whole number of days left until X months is up, from the
 * start date. Any fractional days left will always be rounded down.
 * Will return 0 if today's date is the last day, after that will
 * always return -1 (indicating expired).
 * @param param0 - object with startDate and monthsDuration
 * @returns number of days left
 */
export const wholeTimeUnitsRemaining = ({
  startDate,
  monthsDuration,
}: UnitsRemainingProps): Opt<TimeAmount> => {
  if (!monthsDuration || !startDate.isValid) {
    return undefined;
  }
  const now = DateTime.now();
  const expiryDate = startDate.plus({ months: monthsDuration });

  const round = (val: number) => Math.max(Math.round(val), -1);
  const floor = (val: number) => Math.max(Math.floor(val), -1);

  // NOTE: floor values for days, round for months and years:
  // 0.9 days remaining => 0 days remaining
  // 1.2 days remaining => 1 day remaining
  // 5.6 months remaining => 6 months remaining
  // 5.4 months remaining => 5 months remaining
  // years will behave similarly to months

  const days = floor(expiryDate.diff(now, 'days').as('days'));
  const months = round(expiryDate.diff(now, 'months').as('months'));
  const years = round(expiryDate.diff(now, 'years').as('years'));

  const units = days < 62 ? 'days' : months < 12 ? 'months' : 'years';
  return { units, duration: { days, months, years }[units] };
};

/**
 * Converts any standard HTML color value to a hex string
 * @param color - input standard HTML color value in any format
 * @returns - color value formatted into a hex string
 */
export const colorToHex = (color: string): string => {
  const ctx = document
    .createElement('canvas')
    .getContext('2d') as CanvasRenderingContext2D;
  ctx.fillStyle = color;
  return ctx.fillStyle;
};

type PickFromUnionBy<
  K extends string,
  T extends Record<K, string>,
  V extends string
> = T extends Record<K, V> ? T : never;
type KeyBy<
  K extends string,
  A extends readonly Record<K, string>[]
> = {
  [key in A[number][K]]: PickFromUnionBy<K, A[number], key>;
};
export const keyByName = <T extends Record<'name', string>>(
  objArray: readonly T[]
): KeyBy<'name', typeof objArray> =>
  _.keyBy(objArray, s => s.name) as KeyBy<'name', typeof objArray>;
